/**
 * KND-2: the ordinary termination by the tenant, one tenancy at a time.
 *
 * Three commands on the KND-1 endpoints. All three answer with the SAME
 * response shape (`TenancyTerminationResponse`: `state`, the canonical
 * termination signals of the tenancy and the stored record), so a caller
 * never needs a second request to see what a write did.
 *
 * The request body carries only what the admin decides. `terminated_by`, the
 * snapshot, actors and timestamps are server-authoritative and a 422 if sent.
 */
import { API_BASE_URL, getApiHeaders, parseAdminErrorResponse } from "./core";

function terminationUrl(tenancyId, suffix = "") {
  return `${API_BASE_URL}/api/admin/tenancies/${encodeURIComponent(
    tenancyId
  )}/termination${suffix}`;
}

/** GET /api/admin/tenancies/{tenancyId}/termination */
export async function fetchAdminTenancyTermination(tenancyId) {
  const res = await fetch(terminationUrl(tenancyId), {
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Kündigung konnte nicht geladen werden.");
  }
  return res.json();
}

/**
 * PUT /api/admin/tenancies/{tenancyId}/termination
 *
 * The full editable representation: an optional field left out is stored as
 * empty, and a missing `latest_checkout_at` means the server default (the
 * termination date at 10:00 Europe/Zurich).
 */
export async function upsertAdminTenancyTermination(tenancyId, payload) {
  const res = await fetch(terminationUrl(tenancyId), {
    method: "PUT",
    headers: getApiHeaders(),
    body: JSON.stringify(payload || {}),
  });
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Kündigung konnte nicht gespeichert werden.");
  }
  return res.json();
}

/** POST /api/admin/tenancies/{tenancyId}/termination/withdraw */
export async function withdrawAdminTenancyTermination(tenancyId) {
  const res = await fetch(terminationUrl(tenancyId, "/withdraw"), {
    method: "POST",
    headers: getApiHeaders(),
  });
  if (!res.ok) {
    const detail = await parseAdminErrorResponse(res);
    throw new Error(detail || "Kündigung konnte nicht zurückgenommen werden.");
  }
  return res.json();
}
